import { Button, Col, Flex, Row, Typography, Drawer, Grid, Menu, Tooltip } from "antd";
import { Link, useLocation } from "react-router";
import { MenuOutlined, PlusOutlined } from "@ant-design/icons";
import { MdDirectionsWalk } from "react-icons/md";
import logo from "@/assets/images/logo.png";
import { lazy, Suspense, useState, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import LanguageSelect from "@/components/common/LanguageSelect";
import JoinWalkButton from "@/components/join-a-walk/JoinWalkButton";
import T from "@/components/common/T";
import useLocalStore from "@/hooks/useLocalStore";
import { useGlobalStore } from "@/context/useGlobalStore";
import useTranslation from "@/hooks/useTranslation";

const { useBreakpoint } = Grid;
const loadCreateWalk = () =>
    import("@/components/walks/CreateWalk").then((module) => ({ default: module.CreateWalk }));
const loadSponsorsPage = () => import("@/pages/Sponsors");

const CreateWalk = lazy(loadCreateWalk);

// Nav-link button styles — the horizontal (desktop) links are compact and
// centered, while the vertical (drawer) links fill the row and left-align.
const NAV_LINK_STYLE = {
    horizontal: {
        fontWeight: 600,
        fontSize: 15,
        color: "#4a4a4a",
        padding: "0 4px"
    },
    vertical: {
        fontWeight: 600,
        fontSize: 16,
        color: "#4a4a4a",
        width: "100%",
        textAlign: "left",
        justifyContent: "left"
    }
};

// Base gradient button style (desktop)
const BASE_JOIN_STYLE = {
    background: "linear-gradient(90deg, #ff4d6d 0%, #ff6a88 100%)",
    border: "none",
    borderRadius: 999,
    fontWeight: 600,
    boxShadow: "0 4px 12px rgba(255, 77, 109, 0.28)"
};

// Full-width primary CTA for the mobile menu drawer.
const DRAWER_CTA_STYLE = {
    ...BASE_JOIN_STYLE,
    width: "100%",
    height: 46,
    fontSize: 16,
    color: "#fff"
};

// Thin vertical rule separating the centered nav from the actions cluster.
const HEADER_DIVIDER_STYLE = {
    width: 1,
    height: 26,
    background: "#e6e6e6",
    flexShrink: 0
};

// "Join Walk" CTA — standalone solid-blue pill (desktop header only).
const HEADER_JOIN_WALK_STYLE = {
    background: "#1677ff",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    fontWeight: 600,
    letterSpacing: 0.2,
    padding: "0 20px",
    height: 40,
    fontSize: 15
};

// Nav item definitions — single source of truth for links + active matching
const NAV_ITEMS = [
    { key: "home", to: "https://www.nevergiveupday.com/", labelKey: "Home", external: true },
    { key: "about", to: "/about", labelKey: "nav.about" },
    { key: "how_it_works", to: "/how-it-works", labelKey: "nav.how_it_works" },
    { key: "city_proclamations", to: "/city-proclamations", labelKey: "nav.city_proclamations" },
    { key: "sponsors", to: "/sponsors-partners", labelKey: "nav.sponsors" },
    { key: "the_map", to: "/the-map", labelKey: "nav.the_map" },
];

// Separate NavLinks component
const NavLinks = ({mode= "horizontal"}) => {
    const { pathname } = useLocation();

    // Highlight the link that matches the current route (external Home never matches)
    const selectedKeys = useMemo(() => {
        const match = NAV_ITEMS.find((item) => !item.external && item.to === pathname);
        return match ? [match.key] : [];
    }, [pathname]);

    return (
        <Menu
            selectedKeys={selectedKeys}
            items={NAV_ITEMS.map((item) => ({
                key: item.key,
                label: (
                    <Link to={item.to}>
                        <Button type="text" style={NAV_LINK_STYLE[mode]}><T k={item.labelKey} /></Button>
                    </Link>
                ),
            }))}
            mode={mode}
            className="header-menu"
            inlineIndent={24}
            style={{
                justifyContent: mode === "horizontal" ? "center" : "start",
                alignItems: "center",
                flex: "auto",
                minWidth: 0,
                maxWidth: "100%"
            }}
            styles={{
                root: {
                    borderBottom: "unset",
                    background: "transparent",
                    minWidth: 200
                },
                item: {
                    paddingInline: 0,
                    marginInline: mode==="vertical" ? "0px" : 6,
                    // height: "36px",
                }
            }}
        />
    );
};
NavLinks.propTypes = {
    mode: PropTypes.string,
};

// LogoSection receives screens from parent — no second useBreakpoint() subscription
const LogoSection = ({ screens }) => {
    const sizes = useMemo(() => {
        const tier = !screens.sm ? 'xs' : !screens.md ? 'sm' : !screens.lg ? 'md' : 'lg';

        return {
            xs: { gap: 8, logoSize: 40, titleFontSize: 14, badgeFontSize: 14, fontSize: 14, showBadges: false },
            sm: { gap: 12, logoSize: 50, titleFontSize: 16, badgeFontSize: 12, fontSize: 12, showBadges: true },
            md: { gap: 16, logoSize: 50, titleFontSize: 18, badgeFontSize: 14, fontSize: 12, showBadges: true },
            lg: { gap: 24, logoSize: 50, titleFontSize: 20, badgeFontSize: 14, fontSize: 12, showBadges: true }
        }[tier];
    }, [screens.sm, screens.md, screens.lg]);

    return (
        <Link to="/" style={{ width: "" }}>
            <Flex gap={sizes.gap} align="center">
                <img
                    src={logo}
                    style={{
                        width: sizes.logoSize,
                        height: sizes.logoSize,
                        flexShrink: 0
                    }}
                    alt="Never Give Up Day"
                />
                <Flex vertical gap={4} justify="center">
                    <Typography.Title
                        style={{
                            marginBlock: 0,
                            paddingBlock: 0,
                            fontWeight: 700,
                            fontSize: sizes.titleFontSize,
                            lineHeight: 1.2
                        }}
                        level={3}
                    >
                        Never Give Up Day
                    </Typography.Title>
                    {sizes.showBadges && (
                        <Flex gap={4} align="center" style={{ lineHeight: 1 }}>
                            {["Move", "Share", "Inspire"].map((word) => (
                                <Flex key={word} gap={4} align="center">
                                    <span style={{width: 5, height: 5, backgroundColor: "#555", borderRadius: "50%"}}/>
                                    <Typography.Text style={{ color: "#555", lineHeight: 1, fontSize: 12 }}>
                                        {word}
                                    </Typography.Text>
                                </Flex>
                            ))}
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Link>
    );
};
LogoSection.propTypes = {
    screens: PropTypes.object,
};

const HeaderLayout = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const setLocalStates = useLocalStore((s) => s.setStates);
    const createWalkModal = useLocalStore((s) => s.createWalkModal);
    const createWalkOpen = Boolean(createWalkModal?.open);
    const createWalkInitialValues = createWalkModal?.initialValues || null;
    const { t } = useTranslation();
    // Single useBreakpoint() — passed down to LogoSection to avoid a second subscription
    const screens = useBreakpoint();

    const setGlobalState = useGlobalStore((s) => s.setState);

    // Memoized callbacks
    const handleCreateWalkOpen = useCallback(() => {
        // The header CTA starts a fresh create: clear any createWalk state left
        // over from a Join-a-Walk / Pin round-trip so neither the selected theme
        // nor the map pin carries into this flow.
        setGlobalState("createWalk", null);
        setLocalStates({
            createWalkModal: {
                open: true,
                initialValues: null,
            },
        });
    }, [setGlobalState, setLocalStates]);
    const handleCreateWalkClose = useCallback(() => {
        setLocalStates({
            createWalkModal: {
                open: false,
                initialValues: null,
            },
        });
    }, [setLocalStates]);
    const handleMobileMenuOpen = useCallback(() => setMobileMenuOpen(true), []);
    const handleMobileMenuClose = useCallback(() => setMobileMenuOpen(false), []);
    const prefetchCreateWalk = useCallback(() => {
        loadCreateWalk();
    }, []);

    // Responsive button styles
    const buttonStyles = useMemo(() => {
        const isXSmall = !screens.sm;
        const isSmall = screens.sm && !screens.md;
        const isMedium = screens.md && !screens.lg;

        return {
            // "Create Walk" CTA — standalone solid-coral pill.
            desktop: {
                ...BASE_JOIN_STYLE,
                borderRadius: 999,
                padding: "0 20px",
                height: 40,
                fontSize: 15,
                letterSpacing: 0.2,
                boxShadow: "none"
            },
            mobile: {
                ...BASE_JOIN_STYLE,
                padding: isXSmall ? "0 12px" : "0 16px",
                height: isXSmall ? 36 : isMedium ? 40 : 44,
                fontSize: isXSmall ? 13 : isSmall ? 14 : 15
            },
        };
    }, [screens.sm, screens.md, screens.lg]);

    return (
        <>
            <Row
                gutter={8}
                align="middle"
                justify="space-between"
                style={{ width: "100%" }}
            >
                {/* Logo Section — receives screens prop, no second useBreakpoint() */}
                <Col xs={18} sm={18} flex={"300px"} style={{ marginBlock: 5, }}>
                    <LogoSection screens={screens} />
                </Col>

                {/* Desktop Navigation (lg and above) */}
                <Col xs={0} sm={0} md={0} lg={16}>
                    <Flex gap={16} align="center" style={{ width: "100%" }}>
                        {/* Centered primary navigation */}
                        <NavLinks mode="horizontal"/>

                        {/* Divider separating navigation from the actions cluster */}
                        <span style={HEADER_DIVIDER_STYLE} />

                        {/* Grouped actions: language + the two standalone CTA pills */}
                        <Flex gap={12} align="center" style={{ flexShrink: 0 }}>
                            <LanguageSelect/>
                            <div className="header-cta-group">
                                <JoinWalkButton
                                    type="default"
                                    className="header-join-walk-btn"
                                    style={HEADER_JOIN_WALK_STYLE}
                                    icon={<MdDirectionsWalk size={18} />}
                                >
                                    <T k="Join Walk" />
                                </JoinWalkButton>
                                <Tooltip title={t("header.create_walk")}>
                                    <Button
                                        type="primary"
                                        className="header-join-btn"
                                        style={buttonStyles.desktop}
                                        icon={<PlusOutlined />}
                                        onClick={handleCreateWalkOpen}
                                        onMouseEnter={prefetchCreateWalk}
                                        onFocus={prefetchCreateWalk}
                                    >
                                        <T k="header.create_walk" />
                                    </Button>
                                </Tooltip>
                            </div>
                        </Flex>
                    </Flex>
                </Col>

                {/* Mobile/Tablet Navigation (md and below) */}
                <Col xs={6} sm={6} md={12} lg={0}>
                    <Flex gap={8} justify="flex-end" align="center">
                        <LanguageSelect/>
                        <JoinWalkButton style={buttonStyles.mobile}>
                            {screens.xs ? <T k="JOIN" /> : <T k="JOIN WALK" />}
                        </JoinWalkButton>
                        <Button
                            type="text"
                            icon={<MenuOutlined />}
                            onClick={handleMobileMenuOpen}
                            style={{ fontSize: 20 }}
                            aria-label="Open navigation menu"
                        />
                    </Flex>
                </Col>
            </Row>

            {/* Mobile Menu Drawer */}
            <Drawer
                title={<T k="Menu" />}
                placement="right"
                onClose={handleMobileMenuClose}
                open={mobileMenuOpen}
                size={280}
                destroyOnHidden
                styles={{
                    body: {
                        padding: 10
                    }
                }}
            >
                <Flex vertical gap={16}>
                    
                    <NavLinks mode="vertical"/>
                    <div style={{marginLeft: 15}}>
                        <LanguageSelect showBtnLang/>
                    </div>
                    <Button
                        type="primary"
                        style={DRAWER_CTA_STYLE}
                        onClick={() => { handleMobileMenuClose(); handleCreateWalkOpen(); }}
                        onMouseEnter={prefetchCreateWalk}
                        onFocus={prefetchCreateWalk}
                    >
                        <T k="header.create_walk" />
                    </Button>
                </Flex>
            </Drawer>

            {/* Create Walk Modal */}
            {createWalkOpen && (
                <Suspense fallback={null}>
                    <CreateWalk
                        open={createWalkOpen}
                        onCancel={handleCreateWalkClose}
                        initialValues={createWalkInitialValues}
                    />
                </Suspense>
            )}
        </>
    );
};

export default memo(HeaderLayout);
